import styled from "styled-components";
import { media } from "../../styles/breakpoints";

interface LayoutStyledProps {
  isMenuOpen?: boolean;
  isLandingPage?: boolean;
}

const LayoutStyled = styled.div<LayoutStyledProps>`
  display: block;
  min-height: 100vh;
  position: relative;

  .header {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 70px;
    background: linear-gradient(
      135deg,
      ${(props) => props.theme.colour.brand.primary} 0%,
      ${(props) => props.theme.colour.brand.secondary} 100%
    );
    padding: 0 ${(props) => props.theme.spacing.md};
    display: flex;
    align-items: center;
    justify-content: space-between;
    z-index: ${(props) => props.theme.zIndex.fixed};
    box-shadow: ${(props) => props.theme.shadows.large};
    /* Safe area para dispositivos con notch */
    padding-top: calc(var(--safe-area-inset-top));
    padding-bottom: calc(var(--safe-area-inset-bottom));

    ${media.tablet`
      height: 80px;
      padding: 0 24px;
    `}

    ${media.desktop`
      height: 90px;
      padding: 0 32px;
    `}

    ${media.wide`
      padding: 0 40px;
    `}

    /* Logo container */
    &__logo-container {
      display: flex;
      align-items: center;
      gap: ${(props) => props.theme.spacing.sm};
      cursor: pointer;
      transition: ${(props) => props.theme.transitions.normal};
      padding: ${(props) => props.theme.spacing.sm};
      border-radius: ${(props) => props.theme.border.borderRadiusLarge};
      text-decoration: none;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        transform: translateY(-1px);
      }

      ${media.tablet`
        gap: 12px;
        padding: 12px;
      `}

      ${media.desktop`
        gap: 16px;
        padding: 16px;
      `}
    }

    &__logo {
      display: flex;
      align-items: center;
      justify-content: center;
      color: ${(props) => props.theme.colour.text.inverse};
      font-size: clamp(2rem, 6vw, 2.5rem);
      font-weight: 800;
      font-family: "Odibee Sans", sans-serif;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      transition: ${(props) => props.theme.transitions.normal};

      ${media.tablet`
        font-size: clamp(2.5rem, 5vw, 3rem);
      `}

      ${media.desktop`
        font-size: clamp(3rem, 4vw, 3.5rem);
      `}

      ${media.wide`
        font-size: 3.5rem;
      `}
    }

    &__sublogo {
      display: flex;
      align-items: baseline;
      color: ${(props) => props.theme.colour.text.inverse};
      font-size: clamp(0.75rem, 2.5vw, 1rem);
      font-weight: 400;
      font-family: "Mansalva", sans-serif;
      opacity: 0.95;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
      transition: ${(props) => props.theme.transitions.normal};

      ${media.tablet`
        font-size: clamp(0.875rem, 3vw, 1.25rem);
      `}

      ${media.desktop`
        font-size: clamp(1rem, 2.5vw, 1.5rem);
      `}

      ${media.wide`
        font-size: 1.5rem;
      `}
    }

    /* Header actions - ahora incluye navbar en desktop */
    &__actions {
      display: flex;
      align-items: center;
      gap: ${(props) => props.theme.spacing.sm};

      ${media.tablet`
        gap: 16px;
      `}

      ${media.desktop`
        gap: 24px;
      `}
    }

    /* Navbar integrado en header para desktop */
    &__navbar {
      display: none;

      ${media.desktop`
        display: flex;
        align-items: center;
        gap: 16px;
      `}

      ${media.wide`
        gap: 24px;
      `}
    }

    /* Responsive menu button - MEJORADO */
    &__menu-toggle {
      display: flex;
      flex-direction: column;
      justify-content: space-around;
      width: 32px;
      height: 32px;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
      transition: ${(props) => props.theme.transitions.normal};
      z-index: ${(props) => props.theme.zIndex.modal};

      ${media.desktop`
        display: none;
      `}

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        transform: scale(1.05);
      }

      &:focus-visible {
        outline: 2px solid ${(props) => props.theme.colour.border.focus};
        outline-offset: 2px;
        background-color: rgba(255, 255, 255, 0.1);
      }

      span {
        width: 100%;
        height: 3px;
        background-color: ${(props) => props.theme.colour.text.inverse};
        border-radius: 2px;
        transition: ${(props) => props.theme.transitions.normal};
        transform-origin: center;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);

        &:first-child {
          transform: ${(props) =>
            props.isMenuOpen
              ? "rotate(45deg) translate(6px, 6px)"
              : "rotate(0)"};
        }

        &:nth-child(2) {
          opacity: ${(props) => (props.isMenuOpen ? "0" : "1")};
          transform: ${(props) =>
            props.isMenuOpen ? "translateX(-10px)" : "translateX(0)"};
        }

        &:last-child {
          transform: ${(props) =>
            props.isMenuOpen
              ? "rotate(-45deg) translate(6px, -6px)"
              : "rotate(0)"};
        }
      }
    }
  }

  /* Navbar separado solo para mobile/tablet - OCULTO EN MOBILE */
  .navbar {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    z-index: ${(props) => props.theme.zIndex.sticky};
    background-color: ${(props) => props.theme.colour.background.dark};
    box-shadow: ${(props) => props.theme.shadows.medium};
    transition: ${(props) => props.theme.transitions.normal};

    /* Ocultar en mobile, solo mostrar en tablet */
    display: none;

    ${media.tablet`
      display: block;
      top: 80px;
    `}

    /* Ocultar navbar en desktop ya que está integrado en header */
    ${media.desktop`
      display: none;
    `}
  }

  /* Mobile menu overlay */
  .mobile-menu {
    position: fixed;
    top: 70px;
    left: 0;
    width: 100%;
    height: calc(100vh - 70px);
    background-color: ${(props) => props.theme.colour.background.dark};
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    z-index: ${(props) => props.theme.zIndex.modal};
    display: flex;
    flex-direction: column;
    padding: 24px;

    ${media.tablet`
      top: 80px;
      height: calc(100vh - 80px);
      padding: 32px;
    `}

    ${media.desktop`
      display: none;
    `}

    &.open {
      transform: translateX(0);
    }

    .mobile-menu__nav {
      display: flex;
      flex-direction: column;
      gap: 24px;
      margin-top: 32px;
    }

    .mobile-menu__item {
      display: flex;
      align-items: center;
      padding: 16px 20px;
      background-color: rgba(255, 255, 255, 0.05);
      border-radius: ${(props) => props.theme.border.borderRadiusLarge};
      color: ${(props) => props.theme.colour.text.inverse};
      text-decoration: none;
      font-size: 1.25rem;
      font-weight: 500;
      transition: ${(props) => props.theme.transitions.normal};
      border: 2px solid transparent;

      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
        transform: translateX(8px);
        border-color: rgba(255, 255, 255, 0.2);
      }

      &.active {
        background-color: ${(props) => props.theme.colour.interactive.primary};
        border-color: ${(props) => props.theme.colour.interactive.hover};
        box-shadow: ${(props) => props.theme.shadows.medium};
      }
    }

    .mobile-menu__icon {
      width: 24px;
      height: 24px;
      margin-right: 16px;
      flex-shrink: 0;
    }
  }

  .content {
    padding: ${(props) => props.theme.spacing.md};
    padding-top: calc(130px + var(--safe-area-inset-top));
    padding-bottom: calc(
      ${(props) => props.theme.spacing.md} + var(--safe-area-inset-bottom)
    );
    min-height: calc(100vh - 130px);
    max-width: 1400px;
    margin: 0 auto;

    ${media.tablet`
      padding: 24px;
      padding-top: calc(140px + var(--safe-area-inset-top));
      padding-bottom: calc(24px + var(--safe-area-inset-bottom));
      min-height: calc(100vh - 140px);
    `}

    ${media.desktop`
      padding: 32px;
      padding-top: calc(150px + var(--safe-area-inset-top));
      padding-bottom: calc(32px + var(--safe-area-inset-bottom));
      min-height: calc(100vh - 150px);
    `}

    ${media.wide`
      padding: 40px;
      padding-top: calc(160px + var(--safe-area-inset-top));
      padding-bottom: calc(40px + var(--safe-area-inset-bottom));
      min-height: calc(100vh - 160px);
    `}

    /* Estilos especiales para la landing page */
    &--landing {
      padding: 0;
      padding-top: 0;
      padding-bottom: 0;
      min-height: 100vh;
      max-width: none;
      margin: 0;
    }
  }

  .not-found {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    text-align: center;
    padding: ${(props) => props.theme.spacing.xl};
  }

  /* Mejorar Toastify */
  .Toastify__close-button > svg {
    min-width: 42px;
    min-height: 42px;
  }

  /* Mejorar accesibilidad del focus */
  *:focus-visible {
    outline: 2px solid ${(props) => props.theme.colour.border.focus};
    outline-offset: 2px;
  }
`;

export default LayoutStyled;
